import io from 'socket.io-client';
import { handleGameError } from '../utils/gameErrorHandler';
import { 
    handleSocketStateError, 
    handleNetworkStatus, 
    wrapSocketEvent, 
    handleSocketActionResult,
    createReconnectHandler 
} from '../utils/socketErrorHandler';

class SocketService {
    constructor() {
        this.socket = null;
        this.isConnected = false;
        this.roomId = null;
        this.playerId = null;
        this.eventListeners = new Map();
        this.reconnectHandler = null;
        this.networkStatusHandler = null;
        this.isConnecting = false; // 연결 중 상태 추가
        this.connectionDebounceTimer = null; // 디바운싱 타이머
        this.DEBOUNCE_DELAY = 100; // 100ms 디바운싱 (더 빠른 응답)
        this.roomInfoUpdateCallbacks = new Set(); // 방 정보 업데이트 콜백들
    }

    // 소켓 연결 (디바운싱 적용)
    connect(url = process.env.REACT_APP_SOCKET_URL) {
        // 이미 연결되어 있거나 연결 중인 경우 중복 시도 방지
        if (this.socket && this.isConnected) {
            console.log('Socket already connected');
            return this.socket;
        }

        if (this.isConnecting) {
            console.log('Socket connection already in progress');
            return this.socket;
        }

        // 디바운싱: 빠른 연속 호출 방지
        if (this.connectionDebounceTimer) {
            clearTimeout(this.connectionDebounceTimer);
        }

        this.connectionDebounceTimer = setTimeout(() => {
            this.isConnecting = true;

            this.socket = io(url, {
                reconnection: true,
                reconnectionDelay: 1000,
                reconnectionDelayMax: 5000,
                maxReconnectionAttempts: 5,
                timeout: 10000,
                forceNew: false
            });

            this._setupSocketEventHandlers();
            this.connectionDebounceTimer = null;
        }, this.DEBOUNCE_DELAY);

        return this.socket;
    }

    // 소켓 이벤트 핸들러 설정 (별도 메서드로 분리)
    _setupSocketEventHandlers() {

        // 재연결 핸들러 초기화
        this.reconnectHandler = createReconnectHandler(this);

        // 네트워크 상태 모니터링 시작
        if (!this.networkStatusHandler) {
            this.networkStatusHandler = handleNetworkStatus();
        }

        // 기본 이벤트 리스너 설정 (오류 처리 포함)
        this.socket.on('connect', () => {
            this.isConnected = true;
            this.isConnecting = false; // 연결 완료 시 연결 중 상태 해제
            if (this.reconnectHandler) {
                this.reconnectHandler.resetReconnectAttempts();
            }

            // 연결 즉시 사용자 인증 시도
            this.authenticateUser();
        });

        this.socket.on('disconnect', wrapSocketEvent('disconnect', (reason) => {
            this.isConnected = false;
            handleSocketStateError('disconnect', reason);
            console.log('Socket disconnected:', reason);
        }));

        this.socket.on('connect_error', wrapSocketEvent('connect_error', (error) => {
            this.isConnected = false;
            handleSocketStateError('connect_error', error);
            console.error('Socket connection error:', error);
        }));

        this.socket.on('reconnect_attempt', wrapSocketEvent('reconnect_attempt', (attemptNumber) => {
            handleSocketStateError('reconnect_attempt', attemptNumber);
        }));

        this.socket.on('reconnect_error', wrapSocketEvent('reconnect_error', (error) => {
            handleSocketStateError('reconnect_error', error);
            console.error('Reconnection error:', error);
        }));

        this.socket.on('reconnect_failed', wrapSocketEvent('reconnect_failed', () => {
            handleSocketStateError('reconnect_failed');
            console.error('Reconnection failed');
        }));

        // 공통 게임 오류 처리 (wrapSocketEvent 제거하여 중복 로그 방지)
        this.socket.on('game_error', (errorData) => {
            handleGameError(errorData);
        });

        // 중복 연결로 인한 강제 해제 처리
        this.socket.on('force_disconnect', (data) => {
            alert(data.message || '다른 브라우저에서 접속하여 연결이 해제됩니다.');
            this.disconnect();
            // 페이지 새로고침으로 새 연결 시도
            window.location.reload();
        });

        return this.socket;
    }

    // 사용자 인증 (소켓 연결 후 호출)
    async authenticateUser() {
        try {
            // sessionStorage에서 사용자 정보 가져오기
            const userStr = sessionStorage.getItem('user');
            if (!userStr) {
                console.warn('No user found in sessionStorage');
                return false;
            }

            const user = JSON.parse(userStr);
            if (!user.id) {
                console.warn('User ID not found');
                return false;
            }

            // 서버에 사용자 인증 요청
            return new Promise((resolve) => {
                this.socket.emit('authenticate_user', { userId: user.id }, (response) => {
                    if (response.error) {
                        console.error('Authentication failed:', response.error);
                        resolve(false);
                    } else {
                        resolve(true);
                    }
                });
            });
        } catch (error) {
            console.error('Authentication error:', error);
            return false;
        }
    }

    // 소켓 연결 해제
    disconnect() {
        if (this.socket) {
            console.log('Disconnecting socket...');
            this.socket.disconnect();
            this.socket = null;
        }
        if (this.socket === null) { // Ensure cleanup even if socket was already null
            this.isConnected = false;
            this.roomId = null;
            this.playerId = null;
            this.eventListeners.clear();

            // 네트워크 상태 모니터링 정리
            if (this.networkStatusHandler) {
                this.networkStatusHandler();
                this.networkStatusHandler = null;
            }
        }
    }

    // 이벤트 리스너 등록
    on(event, callback) {
        if (!this.socket) {
            console.warn('Socket not connected. Call connect() first.');
            return;
        }

        // 기존 리스너가 있다면 제거
        if (this.eventListeners.has(event)) {
            this.socket.off(event, this.eventListeners.get(event));
        }

        this.socket.on(event, callback);
        this.eventListeners.set(event, callback);
    }

    // 이벤트 리스너 제거
    off(event) {
        if (!this.socket) return;

        if (this.eventListeners.has(event)) {
            this.socket.off(event, this.eventListeners.get(event));
            this.eventListeners.delete(event);
        }
    }

    // 이벤트 전송
    emit(event, data, callback) {
        if (!this.socket || !this.isConnected) {
            console.warn('Socket not connected');
            if (callback) callback({ error: 'Socket not connected' });
            return;
        }

        // 콜백이 있는 경우 결과 처리
        if (callback) {
            this.socket.emit(event, data, (result) => {
                if (handleSocketActionResult(event, result)) {
                    callback(result);
                } else {
                    callback({ error: result?.error || '액션 실행 실패' });
                }
            });
        } else {
            this.socket.emit(event, data);
        }
    }

    // 방 입장
    async joinRoom(roomCode, nickname) {
        // 소켓이 항상 연결되어 있다고 가정하므로, 인증은 connect 시점에 이미 완료됨.
        return new Promise((resolve, reject) => {
            let attempts = 0;
            const maxAttempts = 50; // 5초 대기 (100ms * 50)
            
            // 전체 타임아웃 (8초)
            const globalTimeout = setTimeout(() => {
                reject(new Error('방 입장 시간이 초과되었습니다.'));
            }, 8000); // 네트워크 상태가 매우 나쁜 경우를 대비해 유지
            
            // 소켓이 연결될 때까지 대기
            const attemptJoin = () => {
                attempts++;

                if (!this.isConnected) {
                    if (attempts >= maxAttempts) {
                        clearTimeout(globalTimeout); // 타임아웃 클리어
                        reject(new Error('소켓 연결 시간이 초과되었습니다.'));
                        return;
                    }
                    setTimeout(attemptJoin, 100);
                    return;
                }
                
                // 응답 타임아웃 설정 (5초)
                const responseTimeout = setTimeout(() => {
                    clearTimeout(globalTimeout);
                    reject(new Error('방 입장 서버 응답 시간이 초과되었습니다.'));
                }, 5000);
                
                this.emit('join_room', { room_code: roomCode, nickname }, (response) => {
                    clearTimeout(responseTimeout);
                    clearTimeout(globalTimeout);
                    
                    if (response?.error) {
                        reject(new Error(response.error));
                    } else if (!response) {
                        reject(new Error('서버에서 응답을 받지 못했습니다.'));
                    } else {
                        this.roomId = response.room?.room_id;
                        // 방 입장 시 응답에서 player_id 찾기
                        let foundPlayerId = response.player_id;
                        if (!foundPlayerId && response.players) {
                            // players 배열에서 nickname이 일치하는 플레이어 찾기
                            const player = response.players.find(p => p.nickname === nickname);
                            foundPlayerId = player?.player_id;
                        }
                        this.playerId = foundPlayerId || Date.now().toString();
                        
                        // 방 정보 업데이트 콜백 실행
                        this.roomInfoUpdateCallbacks.forEach(callback => {
                            try {
                                callback(this.roomId, this.playerId);
                            } catch (error) {
                                console.error('Room info update callback error:', error);
                            }
                        });
                        
                        resolve(response);
                    }
                });
            };
            
            attemptJoin();
        });
    }

    // 방 생성
    async createRoom(nickname, category, gameType = 'liar') {
        // 소켓이 항상 연결되어 있다고 가정
        return new Promise((resolve, reject) => {
            if (!this.isConnected) {
                return reject(new Error('소켓이 연결되지 않았습니다. 잠시 후 다시 시도해주세요.'));
            }

            const responseTimeout = setTimeout(() => {
                reject(new Error('방 생성 서버 응답 시간이 초과되었습니다.'));
            }, 5000);

            this.emit('create_room', { nickname, category, game_type: gameType }, (response) => {
                clearTimeout(responseTimeout);

                if (response?.error) {
                    reject(new Error(response.error));
                } else if (!response) {
                    reject(new Error('서버에서 응답을 받지 못했습니다.'));
                } else {
                    this.roomId = response.room.room_id;
                    this.playerId = response.player.player_id;
                    
                    // 방 정보 업데이트 콜백 실행
                    this.roomInfoUpdateCallbacks.forEach(callback => {
                        try {
                            callback(this.roomId, this.playerId);
                        } catch (error) {
                            console.error('Room info update callback error on createRoom:', error);
                        }
                    });

                    // 서버에서 create_room 성공 시 자동으로 room에 join 시켜주므로 클라이언트에서 별도 join 불필요.
                    console.log('Room created and joined successfully:', response);
                    resolve(response);
                }
            });
        });
    }

    // 방 나가기
    leaveRoom() {
        return new Promise((resolve, reject) => {
            if (this.roomId && this.playerId) {
                this.emit('leave_room', {
                    room_id: this.roomId,
                    player_id: this.playerId
                }, (response) => {
                    if (response?.error) {
                        console.error('Leave room error:', response.error);
                        reject(new Error(response.error));
                    } else {
                        // 방 정보 초기화
                        this.roomId = null;
                        this.playerId = null;
                        
                        // 방 정보 업데이트 콜백 실행
                        this.roomInfoUpdateCallbacks.forEach(callback => {
                            try {
                                callback(null, null);
                            } catch (error) {
                                console.error('Room info update callback error:', error);
                            }
                        });
                        
                        resolve({ success: true });
                    }
                });
            } else {
                console.warn('No room to leave');
                resolve({ success: true, message: 'No room to leave' });
            }
        });
    }

    // 게임 시작
    startGame() {
        if (!this.roomId || !this.playerId) {
            throw new Error('방 정보가 없습니다. (startGame)');
        }

        this.emit('start_game', {
            room_id: this.roomId,
            player_id: this.playerId
        }, (response) => {
            if (response?.error) {
                console.error('Start game error:', response.error);
            } else if (response?.ok) {
                console.error('Game started successfully');
            }
        });
    }

    // 설명 제출
    submitDescription(text) {
        if (!this.roomId || !this.playerId) {
            throw new Error('방 정보가 없습니다.(submitDescription)');
        }
        
        this.emit('submit_description', {
            room_id: this.roomId,
            player_id: this.playerId,
            text
        });
    }

    // 투표 시작
    startVote() {
        if (!this.roomId) {
            throw new Error('방 정보가 없습니다.(startVote)');
        }
        
        this.emit('start_vote', {
            room_id: this.roomId
        });
    }

    // 투표 제출
    submitVote(targetId) {
        if (!this.roomId || !this.playerId) {
            throw new Error('방 정보가 없습니다.(submitVote)');
        }
        
        this.emit('submit_vote', {
            room_id: this.roomId,
            voter_id: this.playerId,
            target_id: targetId
        });
    }

    // 라이어 마지막 기회
    submitLastChance(guessWord) {
        if (!this.roomId || !this.playerId) {
            throw new Error('방 정보가 없습니다.(submitLastChance)');
        }
        
        this.emit('submit_last_chance', {
            room_id: this.roomId,
            player_id: this.playerId,
            guess_word: guessWord
        });
    }

    // Getter methods
    getSocket() {
        return this.socket;
    }

    getRoomId() {
        return this.roomId;
    }

    getPlayerId() {
        return this.playerId;
    }

    isSocketConnected() {
        return this.socket && this.socket.connected && this.isConnected;
    }

    // 수동으로 room/player 정보 설정 (기존 컴포넌트와의 호환성을 위해)
    setRoomInfo(roomId, playerId) {
        this.roomId = roomId;
        this.playerId = playerId;
        
        // 등록된 콜백들에게 업데이트 알림
        this.roomInfoUpdateCallbacks.forEach(callback => {
            try {
                callback(roomId, playerId);
            } catch (error) {
                console.error('Room info update callback error:', error);
            }
        });
    }
    
    // 방 정보 업데이트 콜백 등록
    onRoomInfoUpdate(callback) {
        this.roomInfoUpdateCallbacks.add(callback);
        return () => this.roomInfoUpdateCallbacks.delete(callback); // 정리 함수 반환
    }
    
    // 방 정보 업데이트 콜백 제거
    offRoomInfoUpdate(callback) {
        this.roomInfoUpdateCallbacks.delete(callback);
    }
}

// 싱글톤 인스턴스 생성
const socketService = new SocketService();

export default socketService;